import type { NewUserQuizCopy } from '../types';

export const EN_NEW_USER_QUIZ_COPY = {
  booking: {
    backToVideo: 'Back to your video',
    bookedSnackbar: 'Your demo is booked — see you there!',
    bullets: {
      pricing: 'Get clear pricing by volume and support level',
      teams: 'Built for teams: reviewers, glossaries, one library',
      setup: 'See the right setup for your content and workflow',
    },
    confirmation: {
      title: 'Your demo is booked',
      description:
        'A calendar invitation has been sent to your email. Your localized preview stays live and shareable before the call.',
    },
    continue: 'Continue',
    decision: {
      bookDemo: {
        title: 'Book a demo',
        buttonLabel: 'Book a demo',
        description:
          'Walk through team workspaces, API, and volume dubbing with a specialist — and map Rask to your pipeline.',
      },
      exploreEditor: {
        title: 'Explore in the editor',
        buttonLabel: 'Open editor',
        description: 'Refine the script, voices, and timing yourself. Everything is unlocked during your trial.',
      },
    },
    description: 'Get Rask dialed in for your content, your team, and your volume.',
    followUp: {
      callAgenda: [
        'A quick look at the preview you made',
        'Your workflow, volume, and rollout plan',
        'We set up the editor around your workflow',
      ],
      callDescription: 'Check your calendar invitation for the confirmed date, time, and meeting link.',
      callDurationLabel: (minutes) => `${minutes}-minute video call`,
      callTitle: 'Your call',
      manageBooking: {
        title: 'Manage your booking',
        buttonLabel: 'Manage booking',
        closeLabel: 'Close',
        description:
          'To reschedule or cancel the meeting, open the event in your corporate calendar and use the options in the invitation.',
        googleCalendarLabel: 'Open corporate Google Calendar',
        outlookCalendarLabel: 'Open corporate Outlook calendar',
      },
      prepareDescription: 'Invite teammates and note any wording, voice, or timing changes you would like to discuss.',
      prepareTitle: 'Prepare for the call',
      share: {
        title: 'Share this preview with your team',
        buttonLabel: 'Share',
        copiedButtonLabel: 'Copied!',
        copyButtonLabel: 'Copy',
        description: 'Send the localized preview to your team so everyone can arrive with useful feedback.',
        modalDescription: 'Send your video to anyone using the link below',
        modalTitle: 'Share your video',
      },
      videoCallLabel: 'Video call',
    },
    heading: 'Book your setup call',
    headingSecondLine: '',
    maybeLater: 'Maybe later',
    persistenceError: "We couldn't save this step. Select Continue to retry.",
    rating: '4.7/5 on G2',
    scheduler: {
      fallbackBookedLabel: 'I booked a time',
      openBookingPage: 'Open booking page',
      loadErrorDescription:
        'Your network or region may be blocking the embed. Open the booking page in a new tab instead.',
      loadErrorTitle: "Couldn't load the scheduler",
      loadingLabel: 'Loading the scheduler',
    },
    trustedBy: 'Trusted by global teams',
    verificationPending: {
      title: 'Confirming your booking',
      description: 'We are checking the meeting details with HubSpot. This can take a few moments.',
    },
  },
  controls: {
    back: 'Back',
    continue: 'Continue',
    continueAnyway: 'Continue anyway',
    progress: (currentStep, totalSteps) => `Question ${currentStep} of ${totalSteps}`,
    submitError: "We couldn't save your answers. Please try again.",
  },
  priorityQuestion: {
    title: 'What is your priority in localization right now?',
    audioTitle: 'What is your priority in localization right now?',
    options: {
      collaborate_with_team_or_reviewers: { label: 'Quality Control and Workflow' },
      consistent_voice_and_terminology: { label: 'Consistent voice and terminology' },
      fast_results_no_setup: { label: 'Simple and quick solo work' },
      integration_into_product_or_workflow: { label: 'Integration and Compliance' },
      not_sure_yet: { label: 'Not sure yet' },
    },
    subtitle: 'Choose one',
  },
  questions: {
    content_team_size: {
      title: 'How many people on your team work with video content?',
      options: {
        just_me: { label: '1' },
        large_team_5_plus: { label: '8+' },
        small_team_2_5: { label: '2-7' },
      },
    },
    localization_frequency: {
      title: 'How often do you localize videos?',
      audioTitle: 'How often do you localize content?',
      options: {
        continuously: { label: 'I have a constant need' },
        occasionally_quarterly: { label: 'Occasionally' },
        regularly_monthly: { label: 'Regularly', description: 'A recurring part of our workflow' },
        one_time_project: { label: 'I have a one-time task' },
      },
    },
  },
} satisfies NewUserQuizCopy;
